/**
 * About OnMarble — the governed panel behind the launcher's product-identity
 * tile (#2328 §3, brand amendment).
 *
 * *"About OnMarble — Marble mark. Opens a governed panel with product identity,
 * public version/build, relevant public status, and legal/privacy destinations
 * appropriate to the current surface."*
 *
 * ── one panel, two hosts ─────────────────────────────────────────────────
 * It is here and not in `apps/console` for the reason the bell is (#1115): the
 * Portal's `brand` mode offers *"the lockup plus About OnMarble only"*, so both
 * hosts show this surface, and an app cannot import from another app. Writing
 * it in the console would have guaranteed a second copy the week the portal
 * needed one.
 *
 * ── it renders what the HOST resolved, and omits the rest ────────────────
 * Data-free like everything in this package: the facts and the destinations
 * arrive as props. What is NOT passed is not drawn — no "unknown", no "—", no
 * skeleton standing in for a value nothing has. That is the same rule the
 * launcher applies to a dead tile, one surface in, and it matters here because
 * *"appropriate to the current principal"* means a portal seat and a console
 * administrator see different amounts of this panel legitimately.
 *
 * MEASURED on this tree: the console can source the deployment's own name and
 * instance type (`core.deployment_identity()`, already read by the navigation
 * shell). There is NO product version/build door and NO registered legal or
 * privacy destination anywhere in the repo, so both are absent today — the
 * props exist so they drop in without reshaping the panel, which is exactly
 * what ruling §2 asks of the host field it forbids rendering.
 *
 * A destination is HTTPS-checked here as well as at its registration door, by
 * the same function the launcher's contact tile uses: this is the last place
 * before a person's browser.
 */
import { MarbleMark } from '../MarbleMark'
import { MoonbaseMark } from '../MoonbaseMark'
import { Modal } from '../controls/Modal'
import { isSafeExternalDestination } from './launcherActions'
import { ONMARBLE_WORDMARK, MOONBASE_MAKER } from './OnMarbleLauncher'
import s from './AboutOnMarblePanel.module.css'

/** One governed fact about this deployment, as the host resolved it. */
export interface AboutFact {
  label: string
  value: string
}

/** One governed destination — legal, privacy, status. HTTPS only. */
export interface AboutLink {
  label: string
  href: string
}

export interface AboutOnMarblePanelProps {
  onClose: () => void
  /** Deployment facts the host could source. An empty list draws no section. */
  facts?: readonly AboutFact[]
  /** Legal/privacy/status destinations. Anything not HTTPS is dropped. */
  links?: readonly AboutLink[]
}

export function AboutOnMarblePanel({ onClose, facts = [], links = [] }: AboutOnMarblePanelProps) {
  const safeLinks = links.filter((link) => isSafeExternalDestination(link.href))
  return (
    <Modal label="About OnMarble" onClose={onClose} testid="about-onmarble" width={380}>
      <div className={s.panel}>
        <div className={s.identity}>
          <MarbleMark size={28} />
          <div className={s.names}>
            <span className={s.product}>{ONMARBLE_WORDMARK}</span>
            {/* The maker, in words and in the registered mark. Not aria-hidden
                here: the launcher's own accessible name carries both identities
                and this panel's does not, so the attribution has to be readable
                rather than decorative. */}
            <span className={s.maker}>
              by <MoonbaseMark size={14} className={s.mb} /> {MOONBASE_MAKER}
            </span>
          </div>
        </div>
        {facts.length > 0
          ? (
            <dl className={s.facts} data-testid="about-onmarble-facts">
              {facts.map((fact) => (
                <div key={fact.label} className={s.fact}>
                  <dt>{fact.label}</dt>
                  <dd>{fact.value}</dd>
                </div>
              ))}
            </dl>
            )
          : null}
        {safeLinks.length > 0
          ? (
            <ul className={s.links} data-testid="about-onmarble-links">
              {safeLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} target="_blank" rel="noreferrer">{link.label}</a>
                </li>
              ))}
            </ul>
            )
          : null}
        <button type="button" className={s.close} onClick={onClose}>Close</button>
      </div>
    </Modal>
  )
}
